import React, { useRef, useEffect, useState, useMemo } from "react";
import "../../css/Home/searchDropDown.css";
import { ReactComponent as ArrowIcon } from '../../assets/icons/svg/arrowUnder.svg';

/*
Dropdown (다중 선택) 요약

- 역할: 상단 검색 영역의 드롭다운 셀렉터 UI. 부모로부터 옵션/선택값을 제어받는 controlled 컴포넌트
- 핵심 동작:
  - 옵션 클릭 시 onChange(id) 호출 후 즉시 닫힘
  - 선택된 태그 영역에서 항목 제거 onRemove(id)
  - 검색 인풋으로 옵션 필터링 (간단한 라벨 include)
  - 로딩 상태(isLoading)일 때 옵션대신 로딩 뷰 표시
  - 라벨 보강: options → fullOptions → availableOptions → globalLabelMap 순으로 라벨 폴백
  - 선택 불가 태그 표시: hasOtherSelection && !isGlobalLoading && !isAvailable 인 경우에만 unavailable-selected 클래스 부여
- 주요 props:
  - options: 현재 표시할 옵션 목록
  - value: 선택된 id 배열 (문자열/숫자 혼재 가능, 내부에서 문자열화)
  - onChange/onRemove: 선택/해제 콜백
  - isOpen/onToggle: 외부에서 열린 상태 제어
  - fullOptions/availableOptions/globalLabelMap: 라벨 & 사용 가능 상태 보강 소스
  - isLoading/isGlobalLoading/hasOtherSelection: 로딩 및 표시 정책 제어
*/

export default function SearchDropdown({
  title,
  options,
  defaultText = "선택하세요",
  onChange,
  onRemove,
  value = [],
  isOpen,
  onToggle,
  fullOptions = [],
  availableOptions = [],
  globalLabelMap = {},
  isLoading = false,
  isGlobalLoading = false,
  hasOtherSelection = false,
  isInitialLoading = false,
  rangeMode = false,
  rangeAnchorId = null,
}) {
  const dropdownRef = useRef(null);
  const inputRef = useRef(null);
  const listRef = useRef(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [labelMap, setLabelMap] = useState({});
  const [activeIndex, setActiveIndex] = useState(-1); // 키보드 네비게이션용 현재 포커스 인덱스

  // 열릴 때 포커스, 닫힐 때 검색어 초기화
  useEffect(() => {
    if (isOpen) {
      setActiveIndex(-1);
      // 렌더 후 포커스 보장
      requestAnimationFrame(() => {
        inputRef.current?.focus();
        // 필요하면 맨 끝으로 커서 이동
        const v = inputRef.current?.value ?? "";
        inputRef.current?.setSelectionRange?.(v.length, v.length);
      });
    } else {
      setSearchTerm("");
      setActiveIndex(-1);
    }
  }, [isOpen]);

  // 외부 클릭 감지하여 닫기 (컨테이너까지 포함해서 내부로 간주)
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!isOpen || !dropdownRef.current) return;
      const containerEl = dropdownRef.current.closest('.dropdown-container');
      const clickedInsideComponent = dropdownRef.current.contains(event.target);
      const clickedInsideContainer = containerEl ? containerEl.contains(event.target) : false;
      if (!clickedInsideComponent && !clickedInsideContainer) onToggle();
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen, onToggle]);

  const handleSelect = (option) => {
    const alreadySelected = Array.isArray(value) && value.some(v => String(v) === String(option.id));
    if (!rangeMode && alreadySelected) return;
    onChange(option.id);
    // 선택된 라벨 캐시 (옵션 변경으로 사라져도 표시 유지)
    setLabelMap((prev) => ({ ...prev, [String(option.id)]: option.label }));
    // 선택 후 닫기 (구간 선택의 첫 클릭은 닫지 않음)
    if (rangeMode && !rangeAnchorId) {
      setActiveIndex(-1);
      return;
    }
    if (isOpen && typeof onToggle === 'function') {
      onToggle();
    }
    setActiveIndex(-1);
  };

  const handleSearchKeyDown = (event) => { // 검색 인풋에서 방향키/엔터로 옵션 제어
    if (event.key === 'ArrowDown') {
      if (!filteredOptions.length) return;
      event.preventDefault();
      setActiveIndex(prev => {
        const nextIndex = prev + 1;
        return nextIndex >= filteredOptions.length ? filteredOptions.length - 1 : nextIndex;
      });
    } else if (event.key === 'ArrowUp') {
      if (!filteredOptions.length) return;
      event.preventDefault();
      setActiveIndex(prev => {
        const nextIndex = prev - 1;
        return nextIndex < 0 ? -1 : nextIndex;
      });
    } else if (event.key === 'Enter') {
      if (activeIndex >= 0 && activeIndex < filteredOptions.length) {
        event.preventDefault();
        handleSelect(filteredOptions[activeIndex]);
      }
    }
  };

  const handleSearchInputChange = (event) => { // 검색어 변경 시 리스트 및 포커스 초기화
    setSearchTerm(event.target.value);
    setActiveIndex(-1);
  };

  useEffect(() => { // 키보드로 이동 시 활성 옵션이 스크롤 영역 안에 들어오도록 보정
    if (!isOpen || activeIndex < 0) return;
    const container = listRef.current;
    if (!container) return;
    const activeEl = container.querySelector('.dropdown-option.keyboard-active');
    activeEl?.scrollIntoView?.({ block: 'nearest' });
  }, [activeIndex, isOpen]);

  // options 변경 시 라벨 캐시 보강
  useEffect(() => {
    if (!options) return;
    setLabelMap((prev) => {
      const next = { ...prev };
      options.forEach((opt) => {
        const key = String(opt.id);
        if (next[key] == null) next[key] = opt.label;
      });
      return next;
    });
  }, [options]);

  // fullOptions 기준으로 선택 라벨 보강 (초기값이 옵션에 없을 때 ID 노출 방지)
  useEffect(() => {
    if (!fullOptions) return;
    setLabelMap((prev) => {
      const next = { ...prev };
      (fullOptions || []).forEach((opt) => {
        const key = String(opt.id);
        if (next[key] == null) next[key] = opt.label;
      });
      return next;
    });
  }, [fullOptions]);

  // availableOptions 기준으로도 라벨 보강 (현재 필터 결과에만 존재하는 항목 커버)
  useEffect(() => {
    if (!availableOptions) return;
    setLabelMap((prev) => {
      const next = { ...prev };
      (availableOptions || []).forEach((opt) => {
        const key = String(opt.id);
        if (next[key] == null) next[key] = opt.label;
      });
      return next;
    });
  }, [availableOptions]);

  // 부모 전역 라벨 캐시 병합
  useEffect(() => {
    if (!globalLabelMap) return;
    setLabelMap(prev => ({ ...globalLabelMap, ...prev }));
  }, [globalLabelMap]);

  const filteredOptions = useMemo(() => (options || []).filter((opt) => {
    if (!searchTerm) return true;
    const label = String(opt.label ?? "").toLowerCase();
    return label.includes(searchTerm.toLowerCase());
  }), [options, searchTerm]);

  useEffect(() => {
    if (!isOpen) {
      setActiveIndex(-1);
      return;
    }
    setActiveIndex(prev => {
      if (!filteredOptions.length) return -1;
      if (prev < 0) return -1;
      const lastIndex = filteredOptions.length - 1;
      return Math.min(prev, lastIndex);
    });
  }, [filteredOptions, isOpen]);

  const showLoading = isLoading || isInitialLoading;

  return (
    <div className="search-dropdown" ref={dropdownRef}>
      <div className="dropdown-block">
        <div className="dropdown-title-row">
          <p className="dropdown-title">
            {isInitialLoading ? <span className="option-loading-spinner" aria-label="로딩중" /> : title}
          </p>
          <ArrowIcon className={`dropdown-arrow ${isOpen ? 'open' : ''}`} />
        </div>
        <div
          className={`dropdown ${value.length ? "selected" : ""}`}
        >
          {/* ✅ 여러개 선택된 값 표시 */}
          {value.length === 0 ? (
            (isInitialLoading ? "정보를 불러오고 있습니다" : defaultText)
          ) : (
            <div className="selected-tags">
              {value.map((val, idx) => {
                const key = String(val);
                const option = options.find(o => String(o.id) === key)
                  || (fullOptions || []).find(o => String(o.id) === key)
                  || (availableOptions || []).find(o => String(o.id) === key);
                const displayLabel = option ? option.label : (labelMap[key] ?? globalLabelMap[key] ?? key);
                const isAvailable = (availableOptions || []).some(o => String(o.id) === key);
                // 전역 로딩 중에는 '선택 불가' 표기를 잠시 보류
                const showUnavailable = hasOtherSelection && !isGlobalLoading && !isAvailable;
                const tagClass = `selected-tag drp ${showUnavailable ? 'unavailable-selected' : ''}`;
                return (
                  <span key={idx} className={tagClass}>
                    {displayLabel}
                    <button
                      type="button"
                      className="remove-btn"
                      onClick={(e) => {
                        e.stopPropagation();
                        onRemove(val);
                      }}
                    >
                      ✕
                    </button>
                  </span>
                );
              })}
            </div>
          )}
          {/* Search input moved below selected tags, only when open */}
          {isOpen && (
            <div className="dropdown-search" onClick={(e) => e.stopPropagation()}>
              <input
                ref={inputRef}
                type="text"
                value={searchTerm}
                onChange={handleSearchInputChange}
                placeholder={`${title} 검색`}
                className="dropdown-search-input"
                onKeyDown={handleSearchKeyDown}
              />
            </div>
          )}
        </div>
        <div className={`search-dropdown-options ${isOpen ? 'open' : ''}`} onClick={(e) => e.stopPropagation()}>
          {showLoading ? (
            <div className="dropdown-loading">로딩중입니다...</div>
          ) : (
            <div className="dropdown-result" ref={listRef}>
              {filteredOptions.length === 0 ? (
                <div className="dropdown-no-result">검색 결과가 없습니다.</div>
              ) : (
                filteredOptions.map((option, index) => {
                  const key = String(option.id);
                  const isSelected = (value || []).some(v => String(v) === key);
                  const isActive = index === activeIndex;
                  const isAnchor = rangeMode && rangeAnchorId && String(rangeAnchorId) === key;
                  const flag = (option.reservation_flg || '').toString().toUpperCase();
                  const isActiveFlag = flag === 'TTA' || flag === 'DPR' || flag === 'OLR';
                  const flagClass = isActiveFlag ? 'flag-active' : 'flag-non';
                  const className = `dropdown-option ${flagClass} ${isSelected ? 'selected-option' : ''} ${isActive ? 'keyboard-active' : ''} ${isAnchor ? 'range-anchor' : ''}`;
                  const style = isSelected ? { backgroundColor: '#0066FF', color: '#FFFFFF' } : undefined;
                  return (
                    <div
                      key={index}
                      className={className}
                      onClick={() => { if (rangeMode || !isSelected) handleSelect(option); }}
                      style={style}
                    >
                      {option.label}
                    </div>
                  );
                })
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
